import React from 'react';
import { Footprints, Users, Trophy, Globe, Heart, Target, Award, BarChart3, Calendar, ArrowRight } from 'lucide-react';

interface SolutionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  image: string;
}

// Instant jump with subtle element animation - NO SCROLL ANIMATION
const smoothScrollTo = (elementId: string, offset: number = 80) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  const elementPosition = element.getBoundingClientRect().top;
  const targetPosition = window.pageYOffset + elementPosition - offset;
  
  // Instantly jump to the target position - NO ANIMATION
  window.scrollTo({
    top: targetPosition,
    behavior: 'instant' as ScrollBehavior
  });
  
  // Small delay to ensure scroll is complete before element animation
  setTimeout(() => {
    // Add subtle animation effect to the target element
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    
    // Trigger animation
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    });
    
    // Clean up after animation
    setTimeout(() => {
      element.style.transition = '';
    }, 600);
  }, 10);
};

const SolutionCard: React.FC<SolutionCardProps> = ({ icon, title, description, link, image }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const targetId = link.replace('#', '');
    smoothScrollTo(targetId);
  };

  return (
    <a
      href={link}
      onClick={handleClick}
      className="group block bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-slate-700 hover:border-purple-500 dark:hover:border-purple-400 overflow-hidden"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
              <div className="text-white">
                {icon}
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white">
              {title}
            </h3>
          </div>
        </div>
      </div>
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          {description}
        </p>
        <div className="flex items-center text-purple-600 dark:text-purple-400 font-medium group-hover:gap-2 transition-all">
          <span>Explore</span>
          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </a>
  );
};

const SolutionsSection: React.FC = () => {
  const solutions = [
    {
      icon: <Footprints className="w-6 h-6" />,
      title: 'Steps Challenge',
      description: 'Drive movement and participation with engaging step-based competitions. Inspire your workforce through friendly competition with dynamic goals and live rankings. Establish flexible targets spanning days, weeks, or months, monitor advancement with comprehensive insights, and recognize milestones with digital achievements and incentives. Ideal for promoting active meetings, stair usage, and cultivating wellness routines during work hours.',
      link: '#steps-challenge',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop',
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Multi-activity Challenges',
      description: 'Build tailored competitions that align with your organization\'s wellness vision and values. Accommodate varied exercise interests from biking to meditation. Craft initiatives focused on jogging, aquatic activities, resistance workouts, or other movement forms. Configure personalized measurements, timeframes, and engagement guidelines. Let team members select pursuits that resonate with their passions and capabilities, driving increased participation and superior results.',
      link: '#multi-activity',
      image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&h=600&fit=crop',
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Team Challenge',
      description: 'Foster unity and constructive rivalry through group wellness endeavors. Strengthen connections while maintaining active lifestyles collectively. Organize groups by division, geography, or shared interests. Monitor collective advancement with combined statistics, nurture team spirit via common objectives, and spark amicable contests among groups. Collaborative challenges enhance colleague connections, elevate team spirit, and establish a unified wellness culture.',
      link: '#team-challenge',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop',
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'One Day Virtual Marathon',
      description: 'Connect your distributed team with a single-day digital running event. Generate impactful moments that unite colleagues regardless of location. Participants can join from any place, finishing the route at their preferred speed during the event. Monitor combined advancement, exchange images and experiences, and honor the group accomplishment. Excellent for organizational milestones, wellness awareness occasions, or connection-building activities that forge enduring bonds.',
      link: '#virtual-marathon',
      image: 'https://images.unsplash.com/photo-1571008887538-b36bb32f4571?w=800&h=600&fit=crop',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Remote Team Wellness',
      description: 'Deliver wellness offerings designed specifically for distributed teams and their evolving work settings. Tackle remote work obstacles with digital exercise sessions, web-based wellness seminars, and online group engagements. Assist home-based workers with posture guidance, rest period alerts, and digital community links. Enable remote groups to preserve work-life harmony and remain engaged regardless of geographic separation.',
      link: '#remote-wellness',
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=600&fit=crop',
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Global Employee Engagement',
      description: 'Deploy programs that accommodate varied cultural and geographic needs, building an interconnected international team. Facilitate multilingual interfaces, time zone flexibility, and culturally-aware wellness approaches. Develop location-based competitions that acknowledge regional celebrations and customs. Provide worldwide rankings while highlighting area-specific accomplishments. Establish a cohesive wellness environment that values differences and delivers welcoming experiences for staff globally.',
      link: '#global-engagement',
      image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800&h=600&fit=crop',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Mental Health & Well-being',
      description: 'Cultivate a workplace that emphasizes psychological wellness through focused tools and caring programs. Offer awareness practices, relaxation techniques, and tension reduction methods. Grant entry to structured meditation guides, respiration practices, and psychological wellness materials. Monitor emotional states and pressure indicators, supply confidential assistance options, and develop an atmosphere where mental fitness equals physical fitness. Assist staff in handling anxiety, enhancing concentration, and preserving emotional stability.',
      link: '#mental-health',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=600&fit=crop',
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Holistic Wellness Program',
      description: 'Adopt a comprehensive strategy for health, covering various facets of a balanced organizational lifestyle. Combine movement activities, dietary advice, rest monitoring, anxiety control, and interpersonal engagement. Design complete wellness paths that tackle every element of staff health. Supply learning materials, customized suggestions, and advancement monitoring spanning all wellness areas. Foster an environment of integrated wellness that assists employees across all life domains.',
      link: '#holistic-wellness',
      image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800&h=600&fit=crop',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Wellness Rewards Program',
      description: 'Encourage beneficial health actions through an incentive framework that honors wellness accomplishments and milestones. Add game-like elements to boost participation. Grant credits for finishing competitions, achieving targets, and sustaining activity patterns. Exchange credits for retail vouchers, organizational swag, additional leave, or philanthropic contributions. Establish progressive accomplishment tiers, unique emblems, and acknowledgment initiatives. Transform wellness into an enjoyable and gratifying experience, inspiring staff to regularly interact with their health objectives.',
      link: '#rewards',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=600&fit=crop',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Health & Fitness Analytics',
      description: 'Leverage data-driven intelligence to monitor and refine organizational wellness initiatives and stimulate broad involvement. Explore extensive control panels featuring enrollment statistics, interaction indicators, health pattern evaluations, and return-on-investment assessments. Discover effective campaigns, comprehend staff inclinations, and formulate evidence-based choices. Produce thorough documentation for executives, observe initiative performance longitudinally, and progressively enhance your wellness approach grounded in actionable intelligence.',
      link: '#analytics',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
    },
  ];

  return (
    <section id="solutions" className="py-20 bg-gray-50 dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Complete Employee Wellness. Unified Platform.
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            From activity monitoring to psychological wellness and organizational competitions — Fitzaa enables your team to excel.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <div key={index} id={solution.link.replace('#', '')}>
              <SolutionCard {...solution} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;

